/**
 * Komponente: StickyCartBar
 * -----------------------------------------------------------------------------
 * - Verwaltet die Sichtbarkeit einer Sticky-Cart-Leiste basierend auf der Scrollposition und den beobachteten Elementen (derzeit: Fußzeile und produktinformationsbezogene Blöcke).
 *
 * - Schaltet die Klasse „is-sticky” automatisch basierend auf der Sichtbarkeit um.
 *
 * Verwendung:
 * - Wird in der Regel auf Produktseiten verwendet, ist aber auch für andere Anwendungsfälle flexibel einsetzbar.
 * - Beispiel für eine Seitenstruktur (Kopf- und Fußzeile sind optional):
 *
 * <header> Kopfzeile Inhalt </header>
 * <div data-js-product-box="">
 *   <!-- Product info content -->
 *   <button js-add-to-cart="">Zum Warenkorb hinzufügen</button>
 * </div>
 * <sticky-cart-bar class="sticky-cart-bar">
 *   
 * </sticky-cart-bar>
 * <footer data-js-footer=""> Inhalt der Fußzeile </footer>
 *
 * Requirements:
 * - Either `data-js-product-box` or `js-add-to-cart` must be present for the
 * sticky logic to function properly.
 */
;(() => {
  if ('customElements' in window && customElements.get('sticky-cart-bar')) {
    return
  }
  class StickyCartBar extends HTMLElement {
    constructor() {
      super()

      /** @type {HTMLElement | null} The footer element to observe. */
      this.footer = null

      /** @type {HTMLElement | null} The reference element to observe for sticky logic. */
      this.referenceElement = null

      /** @type {HTMLElement[]} Array of all the elements to observe. */
      this.elementsToObserve = []

      /** @type {IntersectionObserver | null} IntersectionObserver instance. */
      this.observer = null

      this.handleIntersection = this.handleIntersection.bind(this)
    }

    /**
     * Called when the component is added to the DOM.
     */
    connectedCallback() {
      this.setDefaultState()
      this.setupObservers()
    }

    /**
     * Called when the component is removed from the DOM.
     */
    disconnectedCallback() {
      this.disconnectObservers()
    }

    /**
     * Initializes the default state of the component.
     */
    setDefaultState() {
      this.footer = document.querySelector('[data-js-footer]')
      this.referenceElement =
        document.querySelector('[data-js-product-box]') ||
        document.querySelector('[js-add-to-cart]')

      const htmlNodes = [this.footer, this.referenceElement].filter(
        (el) => el !== null
      )

      this.elementsToObserve.push(...htmlNodes)
      this.observer = new IntersectionObserver(this.handleIntersection, {
        threshold: 0.1,
      })
    }

    /**
     * Handles intersection events from the observer.
     *
     * @param {IntersectionObserverEntry[]} entries - List of intersection events.
     */
    handleIntersection(entries) {
      let isSticky = true

      entries.forEach((entry) => {
        /**
         * - Not sticky if we're above the reference element (either Product
         * Info or AddToCart button)
         * - Not sticky if any observed element is visible
         */
        if (
          window.scrollY <= (this.referenceElement?.offsetTop || 0) ||
          entry.isIntersecting
        ) {
          isSticky = false
        }
      })

      isSticky
        ? this.classList.add('is-sticky')
        : this.classList.remove('is-sticky')
    }

    /**
     * Sets up IntersectionObserver for the specified elements.
     */
    setupObservers() {
      if (!this.elementsToObserve.length) {
        console.error('StickyCartBar: No elements to observe.')

        return
      }

      this.elementsToObserve.forEach((element) => {
        this.observer?.observe(element)
      })
    }

    /**
     * Disconnects the IntersectionObserver.
     */
    disconnectObservers() {
      if (!this.observer || !this.elementsToObserve.length) {
        return
      }

      this.elementsToObserve.forEach((element) => {
        this.observer.unobserve(element)
      })

      this.observer.disconnect()
      this.observer = null
    }
  }

  customElements.define('sticky-cart-bar', StickyCartBar)
})()
